import type { components } from './generated/schema';

export type Direction = components['schemas']['DirectionResponseSchemaLenient'];
export type DirectionCreate = components['schemas']['DirectionCreateSchema'];
export type DirectionUpdate = components['schemas']['DirectionUpdateSchema'];

export type Aggregator = components['schemas']['AggregatorResponseSchemaLenient'] & {
  /** Present on create/update; omitted from list Lenient schema in OpenAPI. */
  smsc_id?: string | null;
  /** Runtime SMPP bind status from API (`active` / `inactive`). */
  connection_status?: 'active' | 'inactive' | string | null;
  /** Human-readable connection details for the status tooltip. */
  status_description?: string | null;
  /** May lag behind OpenAPI. */
  transport_type?: 'TCP' | 'TLS' | string | null;
};
export type AggregatorCreate = Omit<components['schemas']['AggregatorCreateSchema'], 'smsc_id'> & {
  /** Optional on create — backend generates when omitted. */
  smsc_id?: string | null;
  transport_type?: 'TCP' | 'TLS' | string | null;
};
export type AggregatorUpdate = components['schemas']['AggregatorUpdateSchema'] & {
  transport_type?: 'TCP' | 'TLS' | string | null;
};

/** Directions an aggregator can handle (with billing currency). Key = direction_id. */
export type AggregatorDirection = components['schemas']['AggregatorDirectionCurrencyResponseSchemaLenient'] & {
  id?: number | null;
  /** May lag behind OpenAPI — same option set as SMPP client decoding_mode. */
  encoding_mode?: number | null;
};
export type AggregatorDirectionCreate = components['schemas']['AggregatorDirectionCurrencyCreateSchema'] & {
  encoding_mode?: number | null;
};
export type AggregatorDirectionUpdate = components['schemas']['AggregatorDirectionCurrencyUpdateSchema'] & {
  /** Synthetic key for CustomStore (maps to direction_id). */
  id?: number;
  encoding_mode?: number | null;
};

export type Partner = components['schemas']['PartnerResponseSchemaLenient'];
export type PartnerCreate = components['schemas']['PartnerCreateSchema'];
export type PartnerUpdate = components['schemas']['PartnerUpdateSchema'];

export type Manager = components['schemas']['ManagerResponseSchemaLenient'] & {
  password?: string | null;
};
export type ManagerCreate = components['schemas']['ManagerCreateSchema'];
export type ManagerUpdate = components['schemas']['ManagerUpdateSchema'];

/** CDP project UUID bound to the channel (may not be in SMS OpenAPI yet). */
export type Stream = components['schemas']['StreamResponseSchemaLenient'] & {
  project_id?: string | null;
  domain_id?: number | null;
  connection_status?: 'active' | 'inactive' | string | null;
  status_description?: string | null;
  link_handler_key?: string | null;
  cdp_postback_config?: Record<string, unknown> | null;
};
export type StreamCreate = components['schemas']['StreamCreateSchema'] & {
  project_id?: string | null;
  domain_id?: number | null;
  link_handler_key?: string | null;
  cdp_postback_config?: Record<string, unknown> | null;
};
export type StreamUpdate = components['schemas']['StreamUpdateSchema'] & {
  project_id?: string | null;
  domain_id?: number | null;
  link_handler_key?: string | null;
  cdp_postback_config?: Record<string, unknown> | null;
};

/** Accounts form: port/enquire_link_interval as numbers. */
type SmppClientFormFields = {
  password?: string | null;
  port?: number | null;
  enquire_link_interval?: number | null;
};

export type SmppClient = Omit<Stream, 'port' | 'enquire_link_interval'> & SmppClientFormFields;
export type SmppClientCreate = Omit<SmppClient, 'id' | 'created_at' | 'updated_at'>;
export type SmppClientUpdate = SmppClient;

export type StreamGroup = components['schemas']['StreamGroupResponseSchemaLenient'];
export type StreamGroupCreate = components['schemas']['StreamGroupCreateSchema'];
export type StreamGroupUpdate = components['schemas']['StreamGroupUpdateSchema'];

export type GroupSenderName = components['schemas']['GroupSenderNameUpdateSchemaLenient'];
export type GroupSenderNameCreate = components['schemas']['GroupSenderNameCreateSchema'];
export type GroupSenderNameUpdate = components['schemas']['GroupSenderNameUpdateSchema'];

export type GroupMessageTemplate = components['schemas']['GroupMessageTemplateUpdateSchemaLenient'];
export type GroupMessageTemplateCreate = components['schemas']['GroupMessageTemplateCreateSchema'];
export type GroupMessageTemplateUpdate = components['schemas']['GroupMessageTemplateUpdateSchema'];

export type Domain = components['schemas']['DomainResponseSchemaLenient'];
export type DomainCreate = components['schemas']['DomainCreateSchema'];
export type DomainUpdate = components['schemas']['DomainUpdateSchema'];

export type GroupDomain = components['schemas']['GroupDomainUpdateSchemaLenient'];
export type GroupDomainCreate = components['schemas']['GroupDomainCreateSchema'];
export type GroupDomainUpdate = components['schemas']['GroupDomainUpdateSchema'];

/** Aggregator split under a stream group (percentage + optional failover parent). */
export type RoutingChoice = components['schemas']['RoutingChoiceResponseSchemaLenient'];
export type RoutingChoiceCreate = components['schemas']['RoutingChoiceCreateSchema'];
export type RoutingChoiceUpdate = components['schemas']['RoutingChoiceUpdateSchema'];

export type SenderName = components['schemas']['SenderNameResponseSchemaLenient'];
export type SenderNameCreate = components['schemas']['SenderNameCreateSchema'];
export type SenderNameUpdate = components['schemas']['SenderNameUpdateSchema'];

export type MessageTemplate = components['schemas']['MessageTemplateResponseSchemaLenient'];
export type MessageTemplateCreate = components['schemas']['MessageTemplateCreateSchema'];
export type MessageTemplateUpdate = components['schemas']['MessageTemplateUpdateSchema'];

export type MessageType = components['schemas']['MessageTypeUpdateSchemaLenient'];
export type MessageTypeCreate = components['schemas']['MessageTypeCreateSchema'];
export type MessageTypeUpdate = components['schemas']['MessageTypeUpdateSchema'];

export type Keyword = components['schemas']['KeywordsForReplacementUpdateSchemaLenient'];
export type KeywordCreate = components['schemas']['KeywordsForReplacementCreateSchema'];
export type KeywordUpdate = components['schemas']['KeywordsForReplacementUpdateSchema'];

export type OperatorStatsRequest = components['schemas']['OperatorStatsRequest'];
export type CreateSmsPayload = components['schemas']['CreateSMSSchema'];
export type CreateSmsResponse = components['schemas']['SendSMSResponseSchema'];
export type LinkReplacementRequestBody = components['schemas']['RequestBody'];

export type ReportGroupBy =
  | 'user_id'
  | 'manager_id'
  | 'partner_id'
  | 'carrier_codes'
  | 'mcc'
  | 'mnc'
  | 'aggregator_id'
  | 'direction_id'
  | 'country_iso'
  | 'message_type'
  | 'created_day';

export interface ReportQuery {
  group_by?: ReportGroupBy[];
  report_currency?: string;
  format?: 'json' | 'xlsx';
  client_id?: number | null;
  manager_id?: number | null;
  partner_id?: number | null;
  direction_id?: number | null;
  country_iso?: string | null;
  operator?: string | null;
  aggregator_id?: number | null;
  date_from?: string | null;
  date_to?: string | null;
}

/** Admin stats report query (GET /report/?format=json). */
export type StatsReportQuery = ReportQuery;

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Aggregated SMS stats metrics row.
 * Columns with % in UI show: `${count} (${pct}%)`.
 */
export interface StatsRow {
  date: string;
  total: number;
  in_delivery: number;
  in_delivery_pct: number;
  delivered: number;
  delivered_pct: number;
  undelivered: number;
  undelivered_pct: number;
  expired: number;
  expired_pct?: number | null;
  /** @deprecated alias of sell_total */
  sms_cost: number;
  /** @deprecated alias of sell_refund */
  refund: number;
  sell_total: number;
  sell_refund: number;
  buy_total: number;
  buy_refund: number;
  /** Buy-side spent from API (usually buy_total − buy_refund). */
  spent: number;
  profit: number;
  sell_price_per_message?: number | null;
  buy_price_per_message?: number | null;
  fraud_score?: number | null;
  clicks?: number | null;
  /** Fraction 0–1 from API (e.g. 0.10256 = 10.256%). */
  click_rate?: number | null;
  [key: string]: unknown;
}

/** Stats grid row: group_by fields + metrics. */
export interface StatsReportRow extends StatsRow {
  group: Record<string, unknown>;
}

export interface StatsReportResult {
  items: StatsReportRow[];
  group_by: ReportGroupBy[];
  /** Aggregates from API `total` section (null if absent). */
  total: StatsRow | null;
}

export interface DetailsQuery {
  date_from?: string | null;
  date_to?: string | null;
  status?: string | null;
  phone_to?: string | null;
  phone_from?: string | null;
  user_id?: number | null;
  system_id?: string | null;
  message_id?: string | null;
  correlation_id?: string | null;
  /** Same filters as GET /report/ (live /details/ supports these). */
  stream_ids?: number[] | null;
  manager_id?: number | null;
  partner_id?: number | null;
  direction_id?: number | null;
  country_iso?: string | null;
  operator?: string | null;
  aggregator_id?: number | null;
  message_type?: number | null;
  page?: number;
  page_size?: number;
  offset?: number;
}

export interface MessageLogRow {
  id?: string | number;
  __key?: string;
  created_at?: string;
  correlation_id?: string;
  type?: string;
  phone?: string;
  phone_to?: string;
  phone_from?: string;
  status?: string;
  text?: string;
  fraud_score?: number | null;
  [key: string]: unknown;
}

/** Local wall-clock → `YYYY-MM-DD HH:mm:ss` for report/details date filters. */
export function formatDateForApi(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Display helper: `123 (45.2%)` */
export function formatCountWithPct(
  count: number | null | undefined,
  pct: number | null | undefined,
): string {
  const c = count ?? 0;
  if (pct == null || Number.isNaN(pct)) return String(c);
  const p = Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
  return `${c} (${p}%)`;
}

export function formatMoney(
  value: number | null | undefined,
  currency = 'RUB',
  fractionDigits = 2,
): string {
  const n = value ?? 0;
  return `${n.toLocaleString('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} ${currency}`;
}

export interface LinkGetUrlQuery {
  host: string;
  uri: string;
}

/** Shared filters for link replacements list / stats / clicks. */
export interface LinkReplacementsQuery {
  start_date: string;
  end_date: string;
  stream_id?: number | null;
  aggregator_id?: number | null;
  offset?: number;
  limit?: number;
}

/** @deprecated Use LinkReplacementsQuery */
export type LinkStatsQuery = LinkReplacementsQuery;
